// components/EditContactModal.jsx
import React, { useState } from "react";
import { X, Loader } from "lucide-react";
import apiService from "../services/api";
import { useTheme } from "../contexts/ThemeContext";

const EditContactModal = ({ contact, onClose, onSuccess }) => {
  const { isDarkMode } = useTheme();
  const [formData, setFormData] = useState({
    salutation: contact.salutation || "",
    firstName: contact.firstName || "",
    lastName: contact.lastName || "",
    suffix: contact.suffix || "",
    title: contact.title || "",
    department: contact.department || "",
    email: contact.email || "",
    phone: contact.phone || "",
    mobilePhone: contact.mobilePhone || "",
    homePhone: contact.homePhone || "",
    otherPhone: contact.otherPhone || "",
    fax: contact.fax || "",
    assistantName: contact.assistantName || "",
    assistantPhone: contact.assistantPhone || "",
    birthdate: contact.birthdate || "",
    leadSource: contact.leadSource || "",
    level: contact.level || "",
    languages: contact.languages || "",

    // Mailing Address
    mailingStreet: contact.mailingStreet || "",
    mailingCity: contact.mailingCity || "",
    mailingState: contact.mailingState || "",
    mailingPostalCode: contact.mailingPostalCode || "",
    mailingCountry: contact.mailingCountry || "",

    // Other Address
    otherStreet: contact.otherStreet || "",
    otherCity: contact.otherCity || "",
    otherState: contact.otherState || "",
    otherPostalCode: contact.otherPostalCode || "",
    otherCountry: contact.otherCountry || "",

    description: contact.description || "",

    // Preferences
    hasOptedOutOfEmail: contact.hasOptedOutOfEmail || false,
    hasOptedOutOfFax: contact.hasOptedOutOfFax || false,
    doNotCall: contact.doNotCall || false,
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      // Convert empty strings to null for proper database handling
      const sanitizedData = Object.keys(formData).reduce((acc, key) => {
        const value = formData[key];
        // Keep booleans as-is, convert empty strings to null, keep other values
        acc[key] =
          typeof value === "boolean" ? value : value === "" ? null : value;
        return acc;
      }, {});

      await apiService.updateContact(contact.id, sanitizedData);
      onSuccess();
    } catch (err) {
      console.error("Error updating contact:", err);
      setError(err.response?.data?.message || "Failed to update contact");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div
        className={`rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col ${
          isDarkMode
            ? "bg-[#13161B] border border-gray-700"
            : "bg-white border border-gray-300"
        }`}
      >
        {/* Header */}
        <div
          className={`px-6 py-4 border-b flex items-center justify-between ${
            isDarkMode ? "border-gray-700" : "border-gray-200"
          }`}
        >
          <h2
            className={`text-lg font-semibold ${
              isDarkMode ? "text-white" : "text-gray-900"
            }`}
          >
            Edit Contact
          </h2>
          <button
            onClick={onClose}
            className={`transition-colors ${
              isDarkMode
                ? "text-gray-400 hover:text-white"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <div className="flex-1 overflow-y-auto p-6">
          <form onSubmit={handleSubmit} id="contactForm">
            {error && (
              <div
                className={`mb-4 p-3 rounded-lg text-sm ${
                  isDarkMode
                    ? "bg-red-500/20 border border-red-500/30 text-red-400"
                    : "bg-red-50 border border-red-200 text-red-600"
                }`}
              >
                {error}
              </div>
            )}

            {/* Name Information */}
            <div
              className={`px-4 py-2 mb-4 rounded ${
                isDarkMode ? "bg-gray-800/50" : "bg-gray-100"
              }`}
            >
              <h3
                className={`text-sm font-semibold ${
                  isDarkMode ? "text-gray-300" : "text-gray-700"
                }`}
              >
                Name Information
              </h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div>
                <label
                  className={`block text-sm font-medium mb-2 ${
                    isDarkMode ? "text-gray-400" : "text-gray-600"
                  }`}
                >
                  Salutation
                </label>
                <select
                  name="salutation"
                  value={formData.salutation}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 ${
                    isDarkMode
                      ? "bg-[#0C0E12] border-gray-700 text-white"
                      : "bg-white border-gray-300 text-gray-900"
                  }`}
                >
                  <option value="">Select...</option>
                  <option value="Mr.">Mr.</option>
                  <option value="Ms.">Ms.</option>
                  <option value="Mrs.">Mrs.</option>
                  <option value="Dr.">Dr.</option>
                  <option value="Prof.">Prof.</option>
                </select>
              </div>

              <div>
                <label
                  className={`block text-sm font-medium mb-2 ${
                    isDarkMode ? "text-gray-400" : "text-gray-600"
                  }`}
                >
                  First Name
                </label>
                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 ${
                    isDarkMode
                      ? "bg-[#0C0E12] border-gray-700 text-white placeholder-gray-500"
                      : "bg-white border-gray-300 text-gray-900 placeholder-gray-400"
                  }`}
                />
              </div>

              <div>
                <label
                  className={`block text-sm font-medium mb-2 ${
                    isDarkMode ? "text-gray-400" : "text-gray-600"
                  }`}
                >
                  Last Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  required
                  className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 ${
                    isDarkMode
                      ? "bg-[#0C0E12] border-gray-700 text-white placeholder-gray-500"
                      : "bg-white border-gray-300 text-gray-900 placeholder-gray-400"
                  }`}
                />
              </div>

              <div>
                <label
                  className={`block text-sm font-medium mb-2 ${
                    isDarkMode ? "text-gray-400" : "text-gray-600"
                  }`}
                >
                  Suffix
                </label>
                <input
                  type="text"
                  name="suffix"
                  value={formData.suffix}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 ${
                    isDarkMode
                      ? "bg-[#0C0E12] border-gray-700 text-white placeholder-gray-500"
                      : "bg-white border-gray-300 text-gray-900 placeholder-gray-400"
                  }`}
                />
              </div>
            </div>

            {/* Professional Information */}
            <div
              className={`px-4 py-2 mb-4 rounded ${
                isDarkMode ? "bg-gray-800/50" : "bg-gray-100"
              }`}
            >
              <h3
                className={`text-sm font-semibold ${
                  isDarkMode ? "text-gray-300" : "text-gray-700"
                }`}
              >
                Professional Information
              </h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div>
                <label
                  className={`block text-sm font-medium mb-2 ${
                    isDarkMode ? "text-gray-400" : "text-gray-600"
                  }`}
                >
                  Title
                </label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 ${
                    isDarkMode
                      ? "bg-[#0C0E12] border-gray-700 text-white placeholder-gray-500"
                      : "bg-white border-gray-300 text-gray-900 placeholder-gray-400"
                  }`}
                />
              </div>

              <div>
                <label
                  className={`block text-sm font-medium mb-2 ${
                    isDarkMode ? "text-gray-400" : "text-gray-600"
                  }`}
                >
                  Department
                </label>
                <input
                  type="text"
                  name="department"
                  value={formData.department}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 ${
                    isDarkMode
                      ? "bg-[#0C0E12] border-gray-700 text-white placeholder-gray-500"
                      : "bg-white border-gray-300 text-gray-900 placeholder-gray-400"
                  }`}
                />
              </div>

              <div>
                <label
                  className={`block text-sm font-medium mb-2 ${
                    isDarkMode ? "text-gray-400" : "text-gray-600"
                  }`}
                >
                  Level
                </label>
                <select
                  name="level"
                  value={formData.level}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 ${
                    isDarkMode
                      ? "bg-[#0C0E12] border-gray-700 text-white"
                      : "bg-white border-gray-300 text-gray-900"
                  }`}
                >
                  <option value="">Select...</option>
                  <option value="Primary">Primary</option>
                  <option value="Secondary">Secondary</option>
                  <option value="Tertiary">Tertiary</option>
                </select>
              </div>

              <div>
                <label
                  className={`block text-sm font-medium mb-2 ${
                    isDarkMode ? "text-gray-400" : "text-gray-600"
                  }`}
                >
                  Lead Source
                </label>
                <select
                  name="leadSource"
                  value={formData.leadSource}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 ${
                    isDarkMode
                      ? "bg-[#0C0E12] border-gray-700 text-white"
                      : "bg-white border-gray-300 text-gray-900"
                  }`}
                >
                  <option value="">Select...</option>
                  <option value="Web">Web</option>
                  <option value="Phone Inquiry">Phone Inquiry</option>
                  <option value="Partner Referral">Partner Referral</option>
                  <option value="Purchased List">Purchased List</option>
                  <option value="Other">Other</option>
                </select>
              </div>
            </div>

            {/* Contact Information */}
            <div
              className={`px-4 py-2 mb-4 rounded ${
                isDarkMode ? "bg-gray-800/50" : "bg-gray-100"
              }`}
            >
              <h3
                className={`text-sm font-semibold ${
                  isDarkMode ? "text-gray-300" : "text-gray-700"
                }`}
              >
                Contact Information
              </h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div>
                <label
                  className={`block text-sm font-medium mb-2 ${
                    isDarkMode ? "text-gray-400" : "text-gray-600"
                  }`}
                >
                  Email
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 ${
                    isDarkMode
                      ? "bg-[#0C0E12] border-gray-700 text-white placeholder-gray-500"
                      : "bg-white border-gray-300 text-gray-900 placeholder-gray-400"
                  }`}
                />
              </div>

              <div>
                <label
                  className={`block text-sm font-medium mb-2 ${
                    isDarkMode ? "text-gray-400" : "text-gray-600"
                  }`}
                >
                  Phone
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 ${
                    isDarkMode
                      ? "bg-[#0C0E12] border-gray-700 text-white placeholder-gray-500"
                      : "bg-white border-gray-300 text-gray-900 placeholder-gray-400"
                  }`}
                />
              </div>

              <div>
                <label
                  className={`block text-sm font-medium mb-2 ${
                    isDarkMode ? "text-gray-400" : "text-gray-600"
                  }`}
                >
                  Mobile Phone
                </label>
                <input
                  type="tel"
                  name="mobilePhone"
                  value={formData.mobilePhone}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 ${
                    isDarkMode
                      ? "bg-[#0C0E12] border-gray-700 text-white placeholder-gray-500"
                      : "bg-white border-gray-300 text-gray-900 placeholder-gray-400"
                  }`}
                />
              </div>

              <div>
                <label
                  className={`block text-sm font-medium mb-2 ${
                    isDarkMode ? "text-gray-400" : "text-gray-600"
                  }`}
                >
                  Home Phone
                </label>
                <input
                  type="tel"
                  name="homePhone"
                  value={formData.homePhone}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 ${
                    isDarkMode
                      ? "bg-[#0C0E12] border-gray-700 text-white placeholder-gray-500"
                      : "bg-white border-gray-300 text-gray-900 placeholder-gray-400"
                  }`}
                />
              </div>

              <div>
                <label
                  className={`block text-sm font-medium mb-2 ${
                    isDarkMode ? "text-gray-400" : "text-gray-600"
                  }`}
                >
                  Other Phone
                </label>
                <input
                  type="tel"
                  name="otherPhone"
                  value={formData.otherPhone}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 ${
                    isDarkMode
                      ? "bg-[#0C0E12] border-gray-700 text-white placeholder-gray-500"
                      : "bg-white border-gray-300 text-gray-900 placeholder-gray-400"
                  }`}
                />
              </div>

              <div>
                <label
                  className={`block text-sm font-medium mb-2 ${
                    isDarkMode ? "text-gray-400" : "text-gray-600"
                  }`}
                >
                  Fax
                </label>
                <input
                  type="tel"
                  name="fax"
                  value={formData.fax}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 ${
                    isDarkMode
                      ? "bg-[#0C0E12] border-gray-700 text-white placeholder-gray-500"
                      : "bg-white border-gray-300 text-gray-900 placeholder-gray-400"
                  }`}
                />
              </div>
            </div>

            {/* Assistant Information */}
            <div
              className={`px-4 py-2 mb-4 rounded ${
                isDarkMode ? "bg-gray-800/50" : "bg-gray-100"
              }`}
            >
              <h3
                className={`text-sm font-semibold ${
                  isDarkMode ? "text-gray-300" : "text-gray-700"
                }`}
              >
                Assistant Information
              </h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div>
                <label
                  className={`block text-sm font-medium mb-2 ${
                    isDarkMode ? "text-gray-400" : "text-gray-600"
                  }`}
                >
                  Assistant Name
                </label>
                <input
                  type="text"
                  name="assistantName"
                  value={formData.assistantName}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 ${
                    isDarkMode
                      ? "bg-[#0C0E12] border-gray-700 text-white placeholder-gray-500"
                      : "bg-white border-gray-300 text-gray-900 placeholder-gray-400"
                  }`}
                />
              </div>

              <div>
                <label
                  className={`block text-sm font-medium mb-2 ${
                    isDarkMode ? "text-gray-400" : "text-gray-600"
                  }`}
                >
                  Assistant Phone
                </label>
                <input
                  type="tel"
                  name="assistantPhone"
                  value={formData.assistantPhone}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 ${
                    isDarkMode
                      ? "bg-[#0C0E12] border-gray-700 text-white placeholder-gray-500"
                      : "bg-white border-gray-300 text-gray-900 placeholder-gray-400"
                  }`}
                />
              </div>
            </div>

            {/* Mailing Address */}
            <div
              className={`px-4 py-2 mb-4 rounded ${
                isDarkMode ? "bg-gray-800/50" : "bg-gray-100"
              }`}
            >
              <h3
                className={`text-sm font-semibold ${
                  isDarkMode ? "text-gray-300" : "text-gray-700"
                }`}
              >
                Mailing Address
              </h3>
            </div>

            <div className="grid grid-cols-1 gap-4 mb-6">
              <div>
                <label
                  className={`block text-sm font-medium mb-2 ${
                    isDarkMode ? "text-gray-400" : "text-gray-600"
                  }`}
                >
                  Street
                </label>
                <textarea
                  name="mailingStreet"
                  value={formData.mailingStreet}
                  onChange={handleChange}
                  rows={2}
                  className={`w-full px-3 py-2 border rounded resize-none focus:ring-2 focus:ring-blue-500 ${
                    isDarkMode
                      ? "bg-[#0C0E12] border-gray-700 text-white placeholder-gray-500"
                      : "bg-white border-gray-300 text-gray-900 placeholder-gray-400"
                  }`}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDarkMode ? "text-gray-400" : "text-gray-600"
                    }`}
                  >
                    City
                  </label>
                  <input
                    type="text"
                    name="mailingCity"
                    value={formData.mailingCity}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 ${
                      isDarkMode
                        ? "bg-[#0C0E12] border-gray-700 text-white placeholder-gray-500"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-400"
                    }`}
                  />
                </div>

                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDarkMode ? "text-gray-400" : "text-gray-600"
                    }`}
                  >
                    State/Province
                  </label>
                  <input
                    type="text"
                    name="mailingState"
                    value={formData.mailingState}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 ${
                      isDarkMode
                        ? "bg-[#0C0E12] border-gray-700 text-white placeholder-gray-500"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-400"
                    }`}
                  />
                </div>

                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDarkMode ? "text-gray-400" : "text-gray-600"
                    }`}
                  >
                    Postal Code
                  </label>
                  <input
                    type="text"
                    name="mailingPostalCode"
                    value={formData.mailingPostalCode}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 ${
                      isDarkMode
                        ? "bg-[#0C0E12] border-gray-700 text-white placeholder-gray-500"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-400"
                    }`}
                  />
                </div>

                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDarkMode ? "text-gray-400" : "text-gray-600"
                    }`}
                  >
                    Country
                  </label>
                  <input
                    type="text"
                    name="mailingCountry"
                    value={formData.mailingCountry}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 ${
                      isDarkMode
                        ? "bg-[#0C0E12] border-gray-700 text-white placeholder-gray-500"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-400"
                    }`}
                  />
                </div>
              </div>
            </div>

            {/* Other Address */}
            <div
              className={`px-4 py-2 mb-4 rounded ${
                isDarkMode ? "bg-gray-800/50" : "bg-gray-100"
              }`}
            >
              <h3
                className={`text-sm font-semibold ${
                  isDarkMode ? "text-gray-300" : "text-gray-700"
                }`}
              >
                Other Address
              </h3>
            </div>

            <div className="grid grid-cols-1 gap-4 mb-6">
              <div>
                <label
                  className={`block text-sm font-medium mb-2 ${
                    isDarkMode ? "text-gray-400" : "text-gray-600"
                  }`}
                >
                  Street
                </label>
                <textarea
                  name="otherStreet"
                  value={formData.otherStreet}
                  onChange={handleChange}
                  rows={2}
                  className={`w-full px-3 py-2 border rounded resize-none focus:ring-2 focus:ring-blue-500 ${
                    isDarkMode
                      ? "bg-[#0C0E12] border-gray-700 text-white placeholder-gray-500"
                      : "bg-white border-gray-300 text-gray-900 placeholder-gray-400"
                  }`}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDarkMode ? "text-gray-400" : "text-gray-600"
                    }`}
                  >
                    City
                  </label>
                  <input
                    type="text"
                    name="otherCity"
                    value={formData.otherCity}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 ${
                      isDarkMode
                        ? "bg-[#0C0E12] border-gray-700 text-white placeholder-gray-500"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-400"
                    }`}
                  />
                </div>

                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDarkMode ? "text-gray-400" : "text-gray-600"
                    }`}
                  >
                    State/Province
                  </label>
                  <input
                    type="text"
                    name="otherState"
                    value={formData.otherState}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 ${
                      isDarkMode
                        ? "bg-[#0C0E12] border-gray-700 text-white placeholder-gray-500"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-400"
                    }`}
                  />
                </div>

                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDarkMode ? "text-gray-400" : "text-gray-600"
                    }`}
                  >
                    Postal Code
                  </label>
                  <input
                    type="text"
                    name="otherPostalCode"
                    value={formData.otherPostalCode}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 ${
                      isDarkMode
                        ? "bg-[#0C0E12] border-gray-700 text-white placeholder-gray-500"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-400"
                    }`}
                  />
                </div>

                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDarkMode ? "text-gray-400" : "text-gray-600"
                    }`}
                  >
                    Country
                  </label>
                  <input
                    type="text"
                    name="otherCountry"
                    value={formData.otherCountry}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 ${
                      isDarkMode
                        ? "bg-[#0C0E12] border-gray-700 text-white placeholder-gray-500"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-400"
                    }`}
                  />
                </div>
              </div>
            </div>

            {/* Additional Information */}
            <div
              className={`px-4 py-2 mb-4 rounded ${
                isDarkMode ? "bg-gray-800/50" : "bg-gray-100"
              }`}
            >
              <h3
                className={`text-sm font-semibold ${
                  isDarkMode ? "text-gray-300" : "text-gray-700"
                }`}
              >
                Additional Information
              </h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div>
                <label
                  className={`block text-sm font-medium mb-2 ${
                    isDarkMode ? "text-gray-400" : "text-gray-600"
                  }`}
                >
                  Birthdate
                </label>
                <input
                  type="date"
                  name="birthdate"
                  value={formData.birthdate}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 ${
                    isDarkMode
                      ? "bg-[#0C0E12] border-gray-700 text-white"
                      : "bg-white border-gray-300 text-gray-900"
                  }`}
                />
              </div>

              <div>
                <label
                  className={`block text-sm font-medium mb-2 ${
                    isDarkMode ? "text-gray-400" : "text-gray-600"
                  }`}
                >
                  Languages
                </label>
                <input
                  type="text"
                  name="languages"
                  value={formData.languages}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 ${
                    isDarkMode
                      ? "bg-[#0C0E12] border-gray-700 text-white placeholder-gray-500"
                      : "bg-white border-gray-300 text-gray-900 placeholder-gray-400"
                  }`}
                />
              </div>
            </div>

            <div className="mb-6">
              <label
                className={`block text-sm font-medium mb-2 ${
                  isDarkMode ? "text-gray-400" : "text-gray-600"
                }`}
              >
                Description
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows={4}
                className={`w-full px-3 py-2 border rounded resize-none focus:ring-2 focus:ring-blue-500 ${
                  isDarkMode
                    ? "bg-[#0C0E12] border-gray-700 text-white placeholder-gray-500"
                    : "bg-white border-gray-300 text-gray-900 placeholder-gray-400"
                }`}
              />
            </div>

            {/* Communication Preferences */}
            <div
              className={`px-4 py-2 mb-4 rounded ${
                isDarkMode ? "bg-gray-800/50" : "bg-gray-100"
              }`}
            >
              <h3
                className={`text-sm font-semibold ${
                  isDarkMode ? "text-gray-300" : "text-gray-700"
                }`}
              >
                Communication Preferences
              </h3>
            </div>

            <div className="space-y-3 mb-6">
              <label
                className={`flex items-center gap-2 text-sm cursor-pointer ${
                  isDarkMode ? "text-gray-300" : "text-gray-700"
                }`}
              >
                <input
                  type="checkbox"
                  name="hasOptedOutOfEmail"
                  checked={formData.hasOptedOutOfEmail}
                  onChange={handleChange}
                  className={`w-4 h-4 rounded border focus:ring-2 focus:ring-blue-500 ${
                    isDarkMode
                      ? "border-gray-700 bg-[#0C0E12] text-blue-600"
                      : "border-gray-300 bg-white text-blue-600"
                  }`}
                />
                Opted out of email
              </label>

              <label
                className={`flex items-center gap-2 text-sm cursor-pointer ${
                  isDarkMode ? "text-gray-300" : "text-gray-700"
                }`}
              >
                <input
                  type="checkbox"
                  name="hasOptedOutOfFax"
                  checked={formData.hasOptedOutOfFax}
                  onChange={handleChange}
                  className={`w-4 h-4 rounded border focus:ring-2 focus:ring-blue-500 ${
                    isDarkMode
                      ? "border-gray-700 bg-[#0C0E12] text-blue-600"
                      : "border-gray-300 bg-white text-blue-600"
                  }`}
                />
                Opted out of fax
              </label>

              <label
                className={`flex items-center gap-2 text-sm cursor-pointer ${
                  isDarkMode ? "text-gray-300" : "text-gray-700"
                }`}
              >
                <input
                  type="checkbox"
                  name="doNotCall"
                  checked={formData.doNotCall}
                  onChange={handleChange}
                  className={`w-4 h-4 rounded border focus:ring-2 focus:ring-blue-500 ${
                    isDarkMode
                      ? "border-gray-700 bg-[#0C0E12] text-blue-600"
                      : "border-gray-300 bg-white text-blue-600"
                  }`}
                />
                Do not call
              </label>
            </div>
          </form>
        </div>

        {/* Footer */}
        <div
          className={`px-6 py-4 border-t flex justify-end gap-3 ${
            isDarkMode
              ? "border-gray-700 bg-[#0C0E12]"
              : "border-gray-200 bg-gray-50"
          }`}
        >
          <button
            type="button"
            onClick={onClose}
            className={`px-4 py-2 text-sm font-medium border rounded transition-colors ${
              isDarkMode
                ? "text-gray-300 bg-[#13161B] border-gray-700 hover:bg-gray-700"
                : "text-gray-700 bg-white border-gray-300 hover:bg-gray-50"
            }`}
          >
            Cancel
          </button>
          <button
            type="submit"
            form="contactForm"
            disabled={loading}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {loading && <Loader size={16} className="animate-spin" />}
            {loading ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditContactModal;
